import { useEffect, useState } from 'react';
import { RefreshCw, Clock, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import { reportsIntelApi } from '@verlo/shared/services/api';
import { useInsight } from '../office';

/**
 * Variance Brief — group-level LLM digest summarising material KPI deltas
 * and multi-site benchmark outliers. Persisted daily per site so re-loads
 * are zero-LLM-cost. Auto-generates today's brief if not already present.
 */
const VarianceBriefCard = ({ site }) => {
  const { enabled: insightOn } = useInsight();
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await reportsIntelApi.getVarianceBrief(site);
      setBrief(res.data.brief);
    } finally {
      setLoading(false);
    }
  };

  const generate = async () => {
    setRefreshing(true);
    try {
      const res = await reportsIntelApi.generateVarianceBrief(site);
      setBrief(res.data.brief);
      toast.success('Variance Brief updated');
    } catch {
      toast.error('Could not generate brief');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [site]);

  // Auto-generate if today's brief is missing
  useEffect(() => {
    if (!insightOn || loading || refreshing) return;
    if (brief) return;
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, brief]);

  if (loading) {
    return (
      <div className="mb-6 h-20 rounded-lg border border-hairline bg-surface-2 animate-pulse" data-testid="variance-brief-loading" />
    );
  }
  // Insight off, or nothing to show: no card. A card saying "no brief yet" is not a brief.
  if (!insightOn || (!brief && !refreshing)) return null;

  return (
    <div
      className="mb-6 relative rounded-lg border border-hairline bg-white"
      data-testid="variance-brief"
    >
      <div className="relative p-5 flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-heading text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint">
              Variance Brief
            </span>
            {brief?.is_yesterday && (
              <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-paper text-ink-soft border border-hairline">
                <Clock size={9} />
                from yesterday
              </span>
            )}
          </div>
          {refreshing && !brief && (
            <p className="text-[13px] text-ink-soft">Verlo is reading outliers and composing your brief…</p>
          )}
          {brief && (() => {
            // Simplification (Wave 4D) — surface only the first sentence by default.
            // Full narrative + highlights hide behind a single "Show detail" toggle.
            const first = brief.insight?.split(/(?<=[.!?])\s+/)[0] || brief.insight || '';
            const hasMore = first && brief.insight && brief.insight.length > first.length;
            return (
              <>
                <p className="text-[14px] leading-relaxed text-ink" data-testid="variance-brief-insight" title={new Date(brief.generated_at).toLocaleString('en-GB')}>
                  {expanded || !hasMore ? brief.insight : first}
                </p>
                {(hasMore || brief.highlights?.length > 0) && (
                  <button
                    onClick={() => setExpanded((v) => !v)}
                    className="mt-2 font-heading text-[11px] font-bold uppercase tracking-[0.06em] text-verlo hover:underline"
                    data-testid="variance-brief-toggle-detail"
                  >
                    {expanded ? 'Show less' : 'Show detail' + (brief.highlights?.length ? ` · ${brief.highlights.length} signals` : '')}
                  </button>
                )}
              </>
            );
          })()}

          {expanded && brief?.highlights?.length > 0 && (
            <ul className="mt-2 space-y-1" data-testid="variance-brief-highlights">
              {brief.highlights.map((h, i) => {
                const negative = /-\d/.test(h) || /below/i.test(h);
                const Icon = negative ? TrendingDown : TrendingUp;
                const cls = negative ? 'text-signal-stop' : 'text-signal-ok';
                return (
                  <li key={i} className="flex items-start gap-2 text-[12.5px] text-ink-soft">
                    <Icon size={11} className={`mt-0.5 ${cls}`} />
                    <span>{h}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <button
          onClick={generate}
          disabled={refreshing}
          className="inline-flex shrink-0 items-center gap-1 font-heading text-[11px] font-bold uppercase tracking-[0.06em] text-verlo hover:underline disabled:opacity-50"
          title="Regenerate the variance brief"
          data-testid="variance-brief-refresh"
        >
          <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Working…' : 'Refresh'}
        </button>
      </div>
    </div>
  );
};

export default VarianceBriefCard;
